'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Image from 'next/image';
import styles from '../styles/Login.module.css';
import { authApi } from '@/lib/api';

const BANNER_IMAGES = [
  '/images/login_asset.jpg',
  '/images/login_Inventory.jpg',
  '/images/login_smarten_ERP.jpg',
  '/images/registration_Slider_Smarten_ERP.jpg',
  '/images/registration_Slider_Inventory.jpg',
  '/images/registration_Slider_asset.jpg',
];

function ResetPasswordContent() {
    const searchParams = useSearchParams();
    const router = useRouter();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const token = searchParams.get('token');
    const [imgIndex, setImgIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setImgIndex(i => (i + 1) % BANNER_IMAGES.length), 5000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing reset token');
        }
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!token) {
            setError('Invalid reset token');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setIsLoading(true);

        try {
            const response = await authApi.resetPassword({ token, newPassword });
            setSuccess(response.message);
            setNewPassword('');
            setConfirmPassword('');

            // Redirect to login after 2 seconds
            setTimeout(() => {
                router.push('/');
            }, 2000);
        } catch (err: any) {
            setError(err.message || 'Failed to reset password');
        } finally {
            setIsLoading(false);
        }
    };

    if (!token) {
        return (
            <div className={styles.pageWrapper}>
                <div className={styles.authCard}>
                    {/* LEFT SIDE - INVALID TOKEN */}
                    <div className={styles.leftPanel}>
                        <div className={styles.loginHeader}>
                            <Image
                                src="/images/logo.svg"
                                alt="Smarten Meet Logo"
                                width={140}
                                height={40}
                                className={styles.logo}
                            />
                            <h1 className={styles.welcomeTitle}>Invalid Reset Link</h1>
                            <p className={styles.welcomeSubtitle}>
                                This password reset link is invalid or has expired.
                            </p>
                        </div>
                        <button
                            onClick={() => router.push('/')}
                            className={styles.submitButton}
                        >
                            Back to Login
                        </button>
                    </div>

                    {/* RIGHT SIDE - IMAGE PANEL */}
                    <div className={styles.rightPanel}>
                        <Image key={imgIndex} src={BANNER_IMAGES[imgIndex]} alt="Image" fill className={styles.illustration} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.authCard}>
                {/* LEFT SIDE - RESET PASSWORD */}
                <div className={styles.leftPanel}>
                    <div className={styles.loginHeader}>
                        <Image
                            src="/images/logo.svg"
                            alt="Smarten Meet Logo"
                            width={140}
                            height={40}
                            className={styles.logo}
                        />
                        <h1 className={styles.welcomeTitle}>Reset Password</h1>
                        <p className={styles.welcomeSubtitle}>
                            Enter your new password
                        </p>
                    </div>

                    {error && (
                        <div className={styles.errorBanner}>
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className={styles.successBanner}>
                            {success}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.loginForm}>
                        <div className={styles.formGroup}>
                            <label htmlFor="newPassword" className={styles.formLabel}>
                                New Password
                            </label>
                            <input
                                type="password"
                                id="newPassword"
                                className={styles.formInput}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                                required
                                disabled={isLoading || !!success}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="confirmPassword" className={styles.formLabel}>
                                Confirm Password
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                className={styles.formInput}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                required
                                disabled={isLoading || !!success}
                            />
                        </div>

                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isLoading || !!success}
                        >
                            {isLoading ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </form>
                </div>

                {/* RIGHT SIDE - IMAGE PANEL */}
                <div className={styles.rightPanel}>
                    <Image key={imgIndex} src={BANNER_IMAGES[imgIndex]} alt="Image" fill className={styles.illustration} />
                </div>
            </div>
        </div>
    );
}

export function ResetPassword() {
    return (
        <Suspense fallback={
            <div className={styles.pageWrapper}>
                <div className={styles.authCard}>
                    <div className={styles.leftPanel}>
                        <div className={styles.loadingScreen}>Loading...</div>
                    </div>
                    <div className={styles.rightPanel}>
                        {/* Empty right panel or could add getImage illustration if desired */}
                    </div>
                </div>
            </div>
        }>
            <ResetPasswordContent />
        </Suspense>
    );
}
